import {useMemo} from "react";
import {Link} from "react-router-dom";
import {MessageCircle, TrendingUp, User, Users} from "lucide-react";
import {cn} from "@/lib/utils";
import {useInfluenceGraph} from "@/hooks/use-influence";
import {SiInstagram, SiX} from "@icons-pack/react-simple-icons";
import {InfluenceGraphNode} from "@/lib/influence-api";

interface Props {
  entityId: string; // format: "platform:username"
  isEditMode?: boolean;
}

interface GraphLink {
  source: string;
  target: string;
  value: number;
  comments: Array<{comment: string; commenter: string}>;
}

function parseEntityId(entityId: string): {platform: "instagram" | "x"; username: string} | null {
  const [platform, ...rest] = entityId.split(":");
  const username = rest.join(":");
  if (platform !== "instagram" && platform !== "x") return null;
  if (!username) return null;
  return {platform, username};
}

function formatNumber(n?: number | null): string {
  if (n === undefined || n === null) return "-";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

export function InfluenceAccountCard({entityId, isEditMode}: Props) {
  const parsed = useMemo(() => parseEntityId(entityId), [entityId]);
  const platform = parsed?.platform ?? "instagram";
  const username = parsed?.username ?? "";

  const {data: graphData} = useInfluenceGraph(platform);

  // Find node in graph data
  const node: InfluenceGraphNode | undefined = useMemo(
    () => graphData?.nodes.find((n) => n.id === username),
    [graphData?.nodes, username]
  );

  // Find who this account influenced (links where this account is source)
  const influencedUsers = useMemo(() => {
    if (!graphData?.links || !username) return [];
    const links = graphData.links as GraphLink[];
    return links
      .filter((link) => link.source === username)
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [graphData, username]);

  // Get recent comments from influenced users
  const recentComments = useMemo(() => {
    if (!graphData?.links || !username) return [];
    const links = graphData.links as GraphLink[];
    const allComments: Array<{username: string; comment: string}> = [];
    for (const link of links) {
      if (link.source === username && link.comments) {
        for (const c of link.comments.slice(0, 2)) {
          allComments.push({username: c.commenter, comment: c.comment});
        }
      }
    }
    return allComments.slice(0, 3);
  }, [graphData, username]);

  if (!parsed) return null;

  const mapUrl = `/influence-map?platform=${platform}&account_id=${username}`;

  return (
    <Link
      to={mapUrl}
      className={cn(
        "bg-card hover:bg-muted/50 border-border group relative flex h-full flex-col rounded-xl border p-4 transition-colors",
        isEditMode && "pointer-events-none"
      )}
    >
      {/* Header */}
      <div className="mb-3 flex items-start gap-3">
        <div className="relative h-10 w-10 shrink-0">
          {node?.profile_image ? (
            <img
              src={node.profile_image}
              alt={username}
              className="border-border h-10 w-10 rounded-full border object-cover"
            />
          ) : (
            <div className="bg-muted flex h-10 w-10 items-center justify-center rounded-full">
              <User className="text-muted-foreground h-5 w-5" />
            </div>
          )}
          <div
            className={cn(
              "absolute right-0 bottom-0 flex h-4 w-4 translate-x-0.5 translate-y-0.5 items-center justify-center rounded-full ring-2 ring-white dark:ring-zinc-900",
              platform === "x"
                ? "bg-black"
                : "bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400"
            )}
          >
            {platform === "x" ? (
              <SiX className="h-2 w-2 text-white" />
            ) : (
              <SiInstagram className="h-2 w-2 text-white" />
            )}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-foreground truncate text-sm font-semibold">@{username}</div>
          <div className="text-muted-foreground flex items-center gap-1 text-xs">
            <Users className="h-3 w-3" />
            {node?.influence_count ?? 0} influenced
          </div>
        </div>
      </div>

      {/* Bio if available */}
      {node?.biography && (
        <p className="text-muted-foreground mb-3 line-clamp-2 text-xs">{node.biography}</p>
      )}

      {/* Profile stats if available */}
      {(node?.followers || node?.following || node?.posts_count) && (
        <div className="text-muted-foreground mb-3 flex gap-3 text-xs">
          {node.followers !== undefined && (
            <span>
              <span className="text-foreground font-medium">{formatNumber(node.followers)}</span>{" "}
              followers
            </span>
          )}
          {node.following !== undefined && (
            <span>
              <span className="text-foreground font-medium">{formatNumber(node.following)}</span>{" "}
              following
            </span>
          )}
        </div>
      )}

      {/* Influenced users list */}
      {influencedUsers.length > 0 && (
        <div className="mb-2 flex-1">
          <div className="text-muted-foreground mb-1 flex items-center gap-1 text-[10px] font-medium tracking-wide uppercase">
            <TrendingUp className="h-3 w-3" />
            Top Influenced
          </div>
          <div className="space-y-0.5">
            {influencedUsers.slice(0, 4).map((link) => (
              <div
                key={link.target}
                className="bg-muted/40 flex items-center justify-between rounded px-2 py-0.5 text-xs"
              >
                <span className="text-foreground truncate">@{link.target}</span>
                <span className="text-muted-foreground shrink-0">{link.value}×</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent comments if no influenced users */}
      {influencedUsers.length === 0 && recentComments.length > 0 && (
        <div className="mb-3 flex-1">
          <div className="text-muted-foreground mb-1.5 flex items-center gap-1 text-[10px] font-medium tracking-wide uppercase">
            <MessageCircle className="h-3 w-3" />
            Recent Interactions
          </div>
          <div className="space-y-1.5">
            {recentComments.map((c, i) => (
              <div key={i} className="bg-muted/40 rounded px-2 py-1.5 text-xs">
                <span className="text-muted-foreground">@{c.username}: </span>
                <span className="text-foreground line-clamp-1">{c.comment}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats footer - always at bottom */}
      <div className="mt-auto grid grid-cols-3 gap-2 pt-1 text-center">
        <div className="bg-muted/50 rounded-lg py-1.5">
          <div className="text-foreground text-base font-bold">{node?.influence_count ?? "-"}</div>
          <div className="text-muted-foreground text-[9px] tracking-wide uppercase">Influenced</div>
        </div>
        <div className="bg-muted/50 rounded-lg py-1.5">
          <div className="text-foreground text-base font-bold">{node?.influenced_by ?? "-"}</div>
          <div className="text-muted-foreground text-[9px] tracking-wide uppercase">By Others</div>
        </div>
        <div className="bg-muted/50 rounded-lg py-1.5">
          <div className="text-foreground text-base font-bold">{node?.comments_made ?? "-"}</div>
          <div className="text-muted-foreground text-[9px] tracking-wide uppercase">Comments</div>
        </div>
      </div>
    </Link>
  );
}
